import React, { useState, useEffect, useRef } from 'react';
import { authenticatedFetch } from '../utils/auth';
import { handleApiError } from '../utils/errorHandler';

// Custom hook for responsive design
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);
  
  return isMobile;
};

const BulkCSV = () => {
  const isMobile = useResponsive();
  const [mainTab, setMainTab] = useState('bulk-processing'); // 'bulk-processing' or 'history'
  const [activeTab, setActiveTab] = useState('search'); // 'search', 'distance', 'distance-between', or 'distance-all'
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [currentJobId, setCurrentJobId] = useState(null); // Track current job ID
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [pendingJobs, setPendingJobs] = useState(new Set()); // Track pending job IDs

  // State for bulk distance
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkReference, setBulkReference] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkResultUrl, setBulkResultUrl] = useState("");
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [bulkAddrSearch, setBulkAddrSearch] = useState("");
  const [bulkDropdownOpen, setBulkDropdownOpen] = useState(false);
  const [bulkHighlightedIndex, setBulkHighlightedIndex] = useState(-1);
  const bulkInputRef = useRef(null);
  const bulkDropdownRef = useRef(null);
  const filteredBulkAddresses = addresses.filter(addr => addr.address.toLowerCase().includes(bulkAddrSearch.toLowerCase())).slice(0, 10);

  // State for bulk distance between
  const [distanceBetweenFile, setDistanceBetweenFile] = useState(null);
  const [distanceBetweenLoading, setDistanceBetweenLoading] = useState(false);
  const [distanceBetweenError, setDistanceBetweenError] = useState("");
  const [distanceBetweenResultUrl, setDistanceBetweenResultUrl] = useState("");
  const [distanceBetweenProgress, setDistanceBetweenProgress] = useState(0);
  const [distanceBetweenJobId, setDistanceBetweenJobId] = useState(null);

  // State for bulk distance all addresses
  const [distanceAllFile, setDistanceAllFile] = useState(null);
  const [distanceAllLoading, setDistanceAllLoading] = useState(false);
  const [distanceAllError, setDistanceAllError] = useState("");
  const [distanceAllResultUrl, setDistanceAllResultUrl] = useState("");
  const [distanceAllProgress, setDistanceAllProgress] = useState(0);
  const [distanceAllJobId, setDistanceAllJobId] = useState(null);

  // State for output field selection (Data Conversion)
  const [includeAddresses, setIncludeAddresses] = useState(true);
  const [includeDigipins, setIncludeDigipins] = useState(true);
  const [includeGeoCoordinates, setIncludeGeoCoordinates] = useState(true);

  // State for job history
  const [jobHistory, setJobHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Helper function to download sample CSV
  const downloadSampleCSV = async (endpoint, filename) => {
    try {
      const response = await authenticatedFetch(endpoint);
      if (!response.ok) {
        throw new Error('Failed to download sample file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading sample CSV:', error);
      alert('Failed to download sample file. Please try again.');
    }
  };

  // Load pending jobs from localStorage and start polling
  useEffect(() => {
    // Load pending jobs from localStorage and start polling
    const storedPendingJobs = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
    if (storedPendingJobs.length > 0) {
      setPendingJobs(new Set(storedPendingJobs));
    }
  }, []);

  // Poll for completed jobs
  useEffect(() => {
    if (pendingJobs.size === 0) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await authenticatedFetch('/bulk-jobs?status=completed');
        if (response.ok) {
          const data = await response.json();
          const completedJobs = data.jobs || [];
          
          // Check if any pending jobs are now completed
          completedJobs.forEach(job => {
            if (pendingJobs.has(job.job_id) && job.status === 'completed') {
              showNotification(job);
              // Remove from pending
              const newPending = new Set(pendingJobs);
              newPending.delete(job.job_id);
              setPendingJobs(newPending);
              
              // Update localStorage
              const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
              const updated = stored.filter(id => id !== job.job_id);
              localStorage.setItem('pendingBulkJobs', JSON.stringify(updated));
            }
          });
        }
      } catch (error) {
        // Ignore polling errors
      }
    }, 5000); // Poll every 5 seconds
    
    return () => clearInterval(pollInterval);
  }, [pendingJobs]);

  // Function to show in-app notification
  const showNotification = (job) => {
    const notification = {
      id: Date.now(),
      jobId: job.job_id,
      type: job.job_type,
      message: `Bulk processing job "${job.filename || 'your file'}" completed successfully!`,
      filename: job.result_filename || 'result.csv',
      timestamp: new Date()
    };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-dismiss after 10 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 10000);
  };

  // Function to download job result
  const downloadJobResult = async (jobId, filename) => {
    try {
      const response = await authenticatedFetch(`/bulk-jobs/${jobId}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading job result:', error);
    }
  };

  // Function to dismiss notification
  const dismissNotification = (notificationId, jobId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    
    // Remove from pending jobs
    const newPending = new Set(pendingJobs);
    newPending.delete(jobId);
    setPendingJobs(newPending);
    
    // Update localStorage
    const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
    const updated = stored.filter(id => id !== jobId);
    localStorage.setItem('pendingBulkJobs', JSON.stringify(updated));
  };

  useEffect(() => {
    // Fetch pre-saved addresses for dropdowns
    const fetchAddresses = async () => {
        try {
            const response = await authenticatedFetch('/addresses');
            if (response.ok) {
                setAddresses(await response.json());
            }
        } catch (error) {
            // Ignore
        }
    };
    if (activeTab === 'distance' || activeTab === 'distance-all') {
      fetchAddresses();
    }
  }, [activeTab]);

  // Fetch job history when history tab is active
  useEffect(() => {
    if (mainTab === 'history') {
      fetchJobHistory();
    }
  }, [mainTab]);

  // Function to fetch job history
  const fetchJobHistory = async () => {
    setLoadingHistory(true);
    setHistoryError('');
    try {
      const response = await authenticatedFetch('/bulk-jobs');
      if (response.ok) {
        const data = await response.json();
        setJobHistory(data.jobs || []);
      } else {
        setHistoryError('Failed to fetch job history');
      }
    } catch (error) {
      setHistoryError('Error loading job history');
      console.error('Error fetching job history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle click outside for bulk dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        bulkDropdownRef.current &&
        !bulkDropdownRef.current.contains(event.target) &&
        bulkInputRef.current &&
        !bulkInputRef.current.contains(event.target)
      ) {
        setBulkDropdownOpen(false);
        setBulkHighlightedIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation for bulk dropdown
  function handleBulkInputKeyDown(e) {
    if (!bulkDropdownOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setBulkHighlightedIndex((prev) => (prev + 1) % filteredBulkAddresses.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setBulkHighlightedIndex((prev) => (prev - 1 + filteredBulkAddresses.length) % filteredBulkAddresses.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (bulkHighlightedIndex >= 0 && bulkHighlightedIndex < filteredBulkAddresses.length) {
        selectBulkAddress(filteredBulkAddresses[bulkHighlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setBulkDropdownOpen(false);
      setBulkHighlightedIndex(-1);
    }
  }

  function selectBulkAddress(addr) {
    setBulkReference(addr.address);
    setBulkAddrSearch(addr.address);
    setBulkDropdownOpen(false);
    setBulkHighlightedIndex(-1);
  }


  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setError('');
      setProgress(0);
    } else {
      setError('Please select a valid CSV file.');
      setFile(null);
    }
  };

  const handleBulkFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setBulkFile(selectedFile);
      setBulkError('');
      setBulkProgress(0);
    } else {
      setBulkError('Please select a valid CSV file.');
      setBulkFile(null);
    }
  };

  const handleDistanceBetweenFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setDistanceBetweenFile(selectedFile);
      setDistanceBetweenError('');
      setDistanceBetweenProgress(0);
    } else {
      setDistanceBetweenError('Please select a valid CSV file.');
      setDistanceBetweenFile(null);
    }
  };

  const handleDistanceAllFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setDistanceAllFile(selectedFile);
      setDistanceAllError('');
      setDistanceAllProgress(0);
    } else {
      setDistanceAllError('Please select a valid CSV file.');
      setDistanceAllFile(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    // Validate that at least one output field is selected
    if (!includeAddresses && !includeDigipins && !includeGeoCoordinates) {
      setError('Please select at least one output field (Addresses, DigiPins, or Geo-coordinates).');
      return;
    }

    setProcessing(true);
    setError('');
    setDownloadUrl('');
    setProgress(0);
    setCurrentJobId(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('include_addresses', includeAddresses ? 'true' : 'false');
      formData.append('include_digipins', includeDigipins ? 'true' : 'false');
      formData.append('include_geo_coordinates', includeGeoCoordinates ? 'true' : 'false');

      const response = await authenticatedFetch('/bulk-csv-stream', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(handleApiError(errorData, 'Failed to process CSV'));
        setProcessing(false);
        return;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        // Background task mode - response is JSON
        const data = await response.json();
        if (data.status === 'queued' && data.job_id) {
          setCurrentJobId(data.job_id);
          setProcessing(false); // Stop showing "Processing..." on the current tab
          
          // Add to pending jobs for polling
          const newPending = new Set(pendingJobs);
          newPending.add(data.job_id);
          setPendingJobs(newPending);
          
          // Store in localStorage
          const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
          if (!stored.includes(data.job_id)) {
            stored.push(data.job_id);
            localStorage.setItem('pendingBulkJobs', JSON.stringify(stored));
          }
        } else if (data.status === 'error') {
          setError(data.message || 'Failed to queue job');
          setProcessing(false);
        }
      } else {
        // Legacy SSE streaming mode (for backwards compatibility)
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                
                if (data.status === 'started') {
                  setProgress(0);
                  if (data.job_id) {
                    setCurrentJobId(data.job_id);
                    const newPending = new Set(pendingJobs);
                    newPending.add(data.job_id);
                    setPendingJobs(newPending);
                    const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
                    if (!stored.includes(data.job_id)) {
                      stored.push(data.job_id);
                      localStorage.setItem('pendingBulkJobs', JSON.stringify(stored));
                    }
                  }
                } else if (data.status === 'progress') {
                  setProgress(data.progress);
                } else if (data.status === 'complete') {
                  setProgress(100);
                  const blob = new Blob([data.csv_data], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  setDownloadUrl(url);
                  setProcessing(false);
                  if (data.job_id) {
                    const newPending = new Set(pendingJobs);
                    newPending.delete(data.job_id);
                    setPendingJobs(newPending);
                    const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
                    const updated = stored.filter(id => id !== data.job_id);
                    localStorage.setItem('pendingBulkJobs', JSON.stringify(updated));
                  }
                  return;
                } else if (data.status === 'error') {
                  setError(data.message);
                  setProcessing(false);
                  if (data.job_id) {
                    const newPending = new Set(pendingJobs);
                    newPending.delete(data.job_id);
                    setPendingJobs(newPending);
                    const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
                    const updated = stored.filter(id => id !== data.job_id);
                    localStorage.setItem('pendingBulkJobs', JSON.stringify(updated));
                  }
                  return;
                }
              } catch (e) {
                // Ignore parsing errors for incomplete chunks
              }
            }
          }
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setProcessing(false);
    }
  };

  // Bulk distance calculation handler
  const handleBulkDistance = async (e) => {
    e.preventDefault();
    setBulkLoading(true);
    setBulkError("");
    setBulkResultUrl("");
    setBulkProgress(0);
    setBulkJobId(null);
    if (!bulkFile || !bulkReference) {
        setBulkError("Please select a CSV file and enter a reference location.");
        setBulkLoading(false);
        return;
    }
    
    try {
        const formData = new FormData();
        formData.append('file', bulkFile);
        formData.append('reference', bulkReference);
        
        const response = await authenticatedFetch('/bulk-distance-csv-stream', {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            setBulkError(handleApiError(errorData, 'Failed to process bulk distance.'));
            setBulkLoading(false);
            return;
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          // Background task mode - response is JSON
          const data = await response.json();
          if (data.status === 'queued' && data.job_id) {
            setBulkJobId(data.job_id);
            setBulkLoading(false);
            
            // Add to pending jobs for polling
            const newPending = new Set(pendingJobs);
            newPending.add(data.job_id);
            setPendingJobs(newPending);
            
            // Store in localStorage
            const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
            if (!stored.includes(data.job_id)) {
              stored.push(data.job_id);
              localStorage.setItem('pendingBulkJobs', JSON.stringify(stored));
            }
          } else if (data.status === 'error') {
            setBulkError(data.message || 'Failed to queue job');
            setBulkLoading(false);
          }
        } else {
          // Legacy SSE streaming mode (for backwards compatibility)
          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          while (true) {
              const { done, value } = await reader.read();
              
              if (done) break;
              
              const chunk = decoder.decode(value);
              const lines = chunk.split('\n');
              
              for (const line of lines) {
                  if (line.startsWith('data: ')) {
                      try {
                          const data = JSON.parse(line.slice(6));
                          
                          if (data.status === 'started') {
                              setBulkProgress(0);
                          } else if (data.status === 'progress') {
                              setBulkProgress(data.progress);
                          } else if (data.status === 'complete') {
                              setBulkProgress(100);
                              const blob = new Blob([data.csv_data], { type: 'text/csv' });
                              const url = window.URL.createObjectURL(blob);
                              setBulkResultUrl(url);
                              setBulkLoading(false);
                              return;
                          } else if (data.status === 'error') {
                              setBulkError(data.message);
                              setBulkLoading(false);
                              return;
                          }
                      } catch (e) {
                          // Ignore parsing errors for incomplete chunks
                      }
                  }
              }
          }
        }
    } catch (err) {
        setBulkError('An error occurred. Please try again.');
        setBulkLoading(false);
    }
  };

  // Bulk distance between handler
  const handleDistanceBetween = async (e) => {
    e.preventDefault();
    setDistanceBetweenLoading(true);
    setDistanceBetweenError("");
    setDistanceBetweenResultUrl("");
    setDistanceBetweenProgress(0);
    setDistanceBetweenJobId(null);
    if (!distanceBetweenFile) {
        setDistanceBetweenError("Please select a CSV file.");
        setDistanceBetweenLoading(false);
        return;
    }
    
    try {
        const formData = new FormData();
        formData.append('file', distanceBetweenFile);
        
        const response = await authenticatedFetch('/bulk-distance-between-csv-stream', {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            setDistanceBetweenError(handleApiError(errorData, 'Failed to process bulk distance between.'));
            setDistanceBetweenLoading(false);
            return;
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          // Background task mode - response is JSON
          const data = await response.json();
          if (data.status === 'queued' && data.job_id) {
            setDistanceBetweenJobId(data.job_id);
            setDistanceBetweenLoading(false);
            
            // Add to pending jobs for polling
            const newPending = new Set(pendingJobs);
            newPending.add(data.job_id);
            setPendingJobs(newPending);
            
            // Store in localStorage
            const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
            if (!stored.includes(data.job_id)) {
              stored.push(data.job_id);
              localStorage.setItem('pendingBulkJobs', JSON.stringify(stored));
            }
          } else if (data.status === 'error') {
            setDistanceBetweenError(data.message || 'Failed to queue job');
            setDistanceBetweenLoading(false);
          }
        } else {
          // Legacy SSE streaming mode (for backwards compatibility)
          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          while (true) {
              const { done, value } = await reader.read();
              
              if (done) break;
              
              const chunk = decoder.decode(value);
              const lines = chunk.split('\n');
              
              for (const line of lines) {
                  if (line.startsWith('data: ')) {
                      try {
                          const data = JSON.parse(line.slice(6));
                          
                          if (data.status === 'started') {
                              setDistanceBetweenProgress(0);
                          } else if (data.status === 'progress') {
                              setDistanceBetweenProgress(data.progress);
                          } else if (data.status === 'complete') {
                              setDistanceBetweenProgress(100);
                              const blob = new Blob([data.csv_data], { type: 'text/csv' });
                              const url = window.URL.createObjectURL(blob);
                              setDistanceBetweenResultUrl(url);
                              setDistanceBetweenLoading(false);
                              return;
                          } else if (data.status === 'error') {
                              setDistanceBetweenError(data.message);
                              setDistanceBetweenLoading(false);
                              return;
                          }
                      } catch (e) {
                          // Ignore parsing errors for incomplete chunks
                      }
                  }
              }
          }
        }
    } catch (err) {
        setDistanceBetweenError('An error occurred. Please try again.');
        setDistanceBetweenLoading(false);
    }
  };

  // Bulk distance all addresses handler
  const handleDistanceAll = async (e) => {
    e.preventDefault();
    setDistanceAllLoading(true);
    setDistanceAllError("");
    setDistanceAllResultUrl("");
    setDistanceAllProgress(0);
    setDistanceAllJobId(null);
    if (!distanceAllFile) {
        setDistanceAllError("Please select a CSV file.");
        setDistanceAllLoading(false);
        return;
    }
    
    try {
        const formData = new FormData();
        formData.append('file', distanceAllFile);
        
        const response = await authenticatedFetch('/bulk-distance-all-addresses-csv-stream', {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            setDistanceAllError(handleApiError(errorData, 'Failed to process bulk distance all addresses.'));
            setDistanceAllLoading(false);
            return;
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          // Background task mode - response is JSON
          const data = await response.json();
          if (data.status === 'queued' && data.job_id) {
            setDistanceAllJobId(data.job_id);
            setDistanceAllLoading(false);
            
            // Add to pending jobs for polling
            const newPending = new Set(pendingJobs);
            newPending.add(data.job_id);
            setPendingJobs(newPending);
            
            // Store in localStorage
            const stored = JSON.parse(localStorage.getItem('pendingBulkJobs') || '[]');
            if (!stored.includes(data.job_id)) {
              stored.push(data.job_id);
              localStorage.setItem('pendingBulkJobs', JSON.stringify(stored));
            }
          } else if (data.status === 'error') {
            setDistanceAllError(data.message || 'Failed to queue job');
            setDistanceAllLoading(false);
          }
        } else {
          // Legacy SSE streaming mode (for backwards compatibility)
          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          while (true) {
              const { done, value } = await reader.read();
              
              if (done) break;
              
              const chunk = decoder.decode(value);
              const lines = chunk.split('\n');
              
              for (const line of lines) {
                  if (line.startsWith('data: ')) {
                      try {
                          const data = JSON.parse(line.slice(6));
                          
                          if (data.status === 'started') {
                              setDistanceAllProgress(0);
                          } else if (data.status === 'progress') {
                              setDistanceAllProgress(data.progress);
                          } else if (data.status === 'complete') {
                              setDistanceAllProgress(100);
                              const blob = new Blob([data.csv_data], { type: 'text/csv' });
                              const url = window.URL.createObjectURL(blob);
                              setDistanceAllResultUrl(url);
                              setDistanceAllLoading(false);
                              return;
                          } else if (data.status === 'error') {
                              setDistanceAllError(data.message);
                              setDistanceAllLoading(false);
                              return;
                          }
                      } catch (e) {
                          // Ignore parsing errors for incomplete chunks
                      }
                  }
              }
          }
        }
    } catch (err) {
        setDistanceAllError('An error occurred. Please try again.');
        setDistanceAllLoading(false);
    }
  };

  // Check if any processing is happening
  const isAnyProcessing = processing || bulkLoading || distanceBetweenLoading || distanceAllLoading;

  // Define radio button options with descriptions
  const processingOptions = [
    {
      id: 'search',
      title: 'Data Conversion',
      description: 'Convert between addresses, DigiPins, and geo-coordinates',
      helpText: 'Upload a CSV with addresses, DigiPins, or coordinates to convert between formats. Perfect for geocoding and reverse geocoding in bulk.'
    },
    {
      id: 'distance',
      title: 'Distance from Reference Point',
      description: 'Calculate distances from one location to multiple destinations',
      helpText: 'Choose a reference location and upload a CSV of destinations to calculate both aerial and road distances with travel times.'
    },
    {
      id: 'distance-between',
      title: 'Point-to-Point Distances',
      description: 'Calculate distances between pairs of locations',
      helpText: 'Upload a CSV with paired locations to calculate distances between each pair. Useful for route planning and logistics.'
    },
    {
      id: 'distance-all',
      title: 'Find Closest Saved Locations',
      description: 'Find the nearest saved location for each input location',
      helpText: 'Upload locations and find which of your saved addresses is closest to each one. Great for territory analysis and service area planning.'
    }
  ];

  const getSelectedOption = () => processingOptions.find(option => option.id === activeTab);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Notifications */}
      {notifications.length > 0 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          maxWidth: '400px'
        }}>
          {notifications.map(notification => (
            <div
              key={notification.id}
              style={{
                background: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                animation: 'slideIn 0.3s ease-out'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', color: '#155724', marginBottom: '4px' }}>
                    Processing Complete! ✅
                  </div>
                  <div style={{ fontSize: '14px', color: '#155724' }}>
                    {notification.message}
                  </div>
                </div>
                <button
                  onClick={() => dismissNotification(notification.id, notification.jobId)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#155724',
                    padding: '0 8px',
                    lineHeight: '1'
                  }}
                >
                  ×
                </button>
              </div>
              <button
                onClick={() => {
                  downloadJobResult(notification.jobId, notification.filename);
                  dismissNotification(notification.id, notification.jobId);
                }}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  width: '100%'
                }}
              >
                Download Results
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
          Batch Processor
        </h1>
        <p style={{ margin: '0', fontSize: '16px', color: '#666' }}>
          {mainTab === 'bulk-processing' ? 'Choose a processing type and upload your CSV file to get started' : 'View and download all your bulk processing job results'}
        </p>
      </div>

      {/* Main Tabs */}
      <div style={{ marginBottom: '24px', borderBottom: '2px solid #e9ecef' }}>
        <div style={{ display: 'flex', gap: '0' }}>
          <button
            onClick={() => setMainTab('bulk-processing')}
            style={{
              padding: '12px 24px',
              background: mainTab === 'bulk-processing' ? '#ffffff' : 'transparent',
              border: 'none',
              borderBottom: mainTab === 'bulk-processing' ? '3px solid #007bff' : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: mainTab === 'bulk-processing' ? '600' : '400',
              color: mainTab === 'bulk-processing' ? '#007bff' : '#666',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
            Bulk Processing
          </button>
          <button
            onClick={() => setMainTab('history')}
            style={{
              padding: '12px 24px',
              background: mainTab === 'history' ? '#ffffff' : 'transparent',
              border: 'none',
              borderBottom: mainTab === 'history' ? '3px solid #007bff' : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: mainTab === 'history' ? '600' : '400',
              color: mainTab === 'history' ? '#007bff' : '#666',
              transition: 'all 0.2s ease',
              marginBottom: '-2px'
            }}
          >
            Job History
          </button>
        </div>
      </div>

      {/* Main Content */}
      {mainTab === 'bulk-processing' ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '24px' : '32px'
        }}>
        
        {/* Left Side - Processing Type Selection */}
        <div style={{ flex: '1', minWidth: '0' }}>
          {!isMobile ? (
            /* Desktop Radio Buttons */
            <div style={{ 
              background: '#f8f9fa', 
              padding: '24px', 
              borderRadius: '12px', 
              border: '1px solid #e9ecef'
            }}>
              <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
                Select Processing Type
              </h3>
              
              <div style={{ display: 'grid', gap: '16px' }}>
                {processingOptions.map((option) => (
                  <label 
                    key={option.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '16px',
                      background: activeTab === option.id ? '#e3f2fd' : '#ffffff',
                      border: activeTab === option.id ? '2px solid #2196f3' : '1px solid #ddd',
                      borderRadius: '8px',
                      cursor: isAnyProcessing ? 'not-allowed' : 'pointer',
                      opacity: isAnyProcessing ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="processingType"
                      value={option.id}
                      checked={activeTab === option.id}
                      onChange={(e) => !isAnyProcessing && setActiveTab(e.target.value)}
                      disabled={isAnyProcessing}
                      style={{
                        marginTop: '2px',
                        width: '18px',
                        height: '18px',
                        cursor: isAnyProcessing ? 'not-allowed' : 'pointer'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#333',
                        marginBottom: '4px'
                      }}>
                        {option.title}
                      </div>
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#666',
                        lineHeight: '1.4',
                        marginBottom: '8px'
                      }}>
                        {option.description}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#2e7d32',
                        background: '#e8f5e8',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        lineHeight: '1.4',
                        border: '1px solid #c8e6c9'
                      }}>
                        💡 {option.helpText}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            /* Mobile Dropdown */
            <div style={{ 
              background: '#f8f9fa', 
              padding: '20px', 
              borderRadius: '12px', 
              border: '1px solid #e9ecef'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
                Select Processing Type
              </h3>
              
              <select
                value={activeTab}
                onChange={(e) => !isAnyProcessing && setActiveTab(e.target.value)}
                disabled={isAnyProcessing}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '16px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  background: '#ffffff',
                  cursor: isAnyProcessing ? 'not-allowed' : 'pointer',
                  opacity: isAnyProcessing ? 0.6 : 1,
                  marginBottom: '16px'
                }}
              >
                {processingOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.title} - {option.description}
                  </option>
                ))}
              </select>

              {/* Mobile Help Text for Selected Option */}
              <div style={{
                fontSize: '14px',
                color: '#2e7d32',
                background: '#e8f5e8',
                padding: '12px 16px',
                borderRadius: '8px',
                lineHeight: '1.5',
                border: '1px solid #c8e6c9'
              }}>
                💡 {getSelectedOption()?.helpText}
              </div>
            </div>
          )}
        </div>

        {/* Right Side - Upload and Help */}
        <div style={{ flex: '1', minWidth: '0' }}>
          {/* Upload Section */}
          <div style={{
            background: '#ffffff',
            border: '1px solid #e9ecef',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
              Upload CSV File
            </h3>
            
            {activeTab === 'search' && (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '500' }}>Select CSV File:</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={isAnyProcessing}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      opacity: isAnyProcessing ? 0.6 : 1,
                      cursor: isAnyProcessing ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {file && (
                    <p style={{ color: '#28a745', fontSize: '14px' }}>
                      Selected: {file.name}
                    </p>
                  )}
                  <div style={{ 
                    padding: '12px', 
                    background: '#f8f9fa', 
                    borderRadius: '4px', 
                    fontSize: '13px', 
                    color: '#666',
                    border: '1px solid #dee2e6',
                    marginTop: '10px'
                  }}>
                    <strong>CSV Format:</strong> Include a column named <code>address</code>, <code>digipin</code>, or <code>latlng</code> (format: "lat,lng").
                    <br />
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => downloadSampleCSV('/sample-csv/bulk-csv/address', 'sample_bulk_csv_address.csv')}
                        style={{ 
                          color: '#007bff', 
                          textDecoration: 'underline', 
                          fontWeight: '500',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 0',
                          fontSize: '13px'
                        }}
                      >
                        📥 Download Sample (Address)
                      </button>
                      <span style={{ color: '#ccc' }}>|</span>
                      <button
                        onClick={() => downloadSampleCSV('/sample-csv/bulk-csv/digipin', 'sample_bulk_csv_digipin.csv')}
                        style={{ 
                          color: '#007bff', 
                          textDecoration: 'underline', 
                          fontWeight: '500',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 0',
                          fontSize: '13px'
                        }}
                      >
                        📥 Download Sample (DigiPin)
                      </button>
                      <span style={{ color: '#ccc' }}>|</span>
                      <button
                        onClick={() => downloadSampleCSV('/sample-csv/bulk-csv/latlng', 'sample_bulk_csv_latlng.csv')}
                        style={{ 
                          color: '#007bff', 
                          textDecoration: 'underline', 
                          fontWeight: '500',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 0',
                          fontSize: '13px'
                        }}
                      >
                        📥 Download Sample (Lat/Lng)
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '500' }}>
                    Select Output Fields:
                  </label>
                  <div style={{ 
                    border: '1px solid #ccc', 
                    borderRadius: '4px', 
                    padding: '12px',
                    backgroundColor: '#fafafa'
                  }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px',
                      cursor: isAnyProcessing ? 'not-allowed' : 'pointer',
                      opacity: isAnyProcessing ? 0.6 : 1
                    }}>
                      <input
                        type="checkbox"
                        checked={includeAddresses}
                        onChange={(e) => !isAnyProcessing && setIncludeAddresses(e.target.checked)}
                        disabled={isAnyProcessing}
                        style={{
                          marginRight: '8px',
                          width: '18px',
                          height: '18px',
                          cursor: isAnyProcessing ? 'not-allowed' : 'pointer'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#333' }}>Addresses</span>
                    </label>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '10px',
                      cursor: isAnyProcessing ? 'not-allowed' : 'pointer',
                      opacity: isAnyProcessing ? 0.6 : 1
                    }}>
                      <input
                        type="checkbox"
                        checked={includeDigipins}
                        onChange={(e) => !isAnyProcessing && setIncludeDigipins(e.target.checked)}
                        disabled={isAnyProcessing}
                        style={{
                          marginRight: '8px',
                          width: '18px',
                          height: '18px',
                          cursor: isAnyProcessing ? 'not-allowed' : 'pointer'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#333' }}>DigiPins</span>
                    </label>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      cursor: isAnyProcessing ? 'not-allowed' : 'pointer',
                      opacity: isAnyProcessing ? 0.6 : 1
                    }}>
                      <input
                        type="checkbox"
                        checked={includeGeoCoordinates}
                        onChange={(e) => !isAnyProcessing && setIncludeGeoCoordinates(e.target.checked)}
                        disabled={isAnyProcessing}
                        style={{
                          marginRight: '8px',
                          width: '18px',
                          height: '18px',
                          cursor: isAnyProcessing ? 'not-allowed' : 'pointer'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#333' }}>Geo-coordinates (lat/lng)</span>
                    </label>
                  </div>
                  {!includeAddresses && !includeDigipins && !includeGeoCoordinates && (
                    <p style={{ color: '#dc3545', fontSize: '12px', marginTop: '8px', marginBottom: '0' }}>
                      Please select at least one output field
                    </p>
                  )}
                </div>

                <button
                  onClick={handleUpload}
                  disabled={!file || processing || (!includeAddresses && !includeDigipins && !includeGeoCoordinates)}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (!file || processing) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    width: '100%',
                    marginBottom: '20px'
                  }}
                >
                  {processing ? 'Processing...' : 'Process CSV'}
                </button>

                {currentJobId && !processing && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    border: '1px solid #90caf9'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      ✓ Job Queued Successfully!
                    </div>
                    <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                      Your job has been submitted and is being processed in the background.
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      Please check the <strong>Job History</strong> tab to view progress and download results when complete.
                    </div>
                  </div>
                )}

                {processing && !currentJobId && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      width: '100%', 
                      backgroundColor: '#e9ecef', 
                      borderRadius: '4px', 
                      overflow: 'hidden',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: `${progress}%`,
                        height: '20px',
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {progress}%
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                      Processing {progress}% complete...
                    </div>
                  </div>
                )}

                {error && (
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    {error}
                  </div>
                )}

                {downloadUrl && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    <h4>Processing Complete!</h4>
                    <p>Your CSV file has been processed successfully.</p>
                    <a
                      href={downloadUrl}
                      download="bulk_search_results.csv"
                      style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        marginTop: '10px'
                      }}
                    >
                      Download Results
                    </a>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'distance' && (
              <div>
                <form onSubmit={handleBulkDistance}>
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '500' }}>Reference Location:</label>
                    <input
                      ref={bulkInputRef}
                      type="text"
                      value={bulkAddrSearch}
                      onChange={e => {
                        setBulkAddrSearch(e.target.value);
                        setBulkDropdownOpen(true);
                        setBulkHighlightedIndex(-1);
                      }}
                      onFocus={() => {
                        setBulkDropdownOpen(true);
                        setBulkHighlightedIndex(-1);
                      }}
                      onKeyDown={handleBulkInputKeyDown}
                      placeholder="Search my location"
                      disabled={isAnyProcessing}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        opacity: isAnyProcessing ? 0.6 : 1,
                        cursor: isAnyProcessing ? 'not-allowed' : 'text'
                      }}
                    />
                    
                    {bulkDropdownOpen && filteredBulkAddresses.length > 0 && (
                      <div
                        ref={bulkDropdownRef}
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderTop: 'none',
                          borderRadius: '0 0 4px 4px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      >
                        {filteredBulkAddresses.map((addr, index) => (
                          <div
                            key={addr.id}
                            onClick={() => selectBulkAddress(addr)}
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                              backgroundColor: index === bulkHighlightedIndex ? '#f0f0f0' : 'white',
                              borderBottom: index < filteredBulkAddresses.length - 1 ? '1px solid #eee' : 'none'
                            }}
                            onMouseEnter={() => setBulkHighlightedIndex(index)}
                          >
                            {addr.address}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '500' }}>Select CSV File:</label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleBulkFileChange}
                      disabled={isAnyProcessing}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        marginBottom: '10px',
                        opacity: isAnyProcessing ? 0.6 : 1,
                        cursor: isAnyProcessing ? 'not-allowed' : 'pointer'
                      }}
                    />
                    {bulkFile && (
                      <p style={{ color: '#28a745', fontSize: '14px' }}>
                        Selected: {bulkFile.name}
                      </p>
                    )}
                    <div style={{ 
                      padding: '12px', 
                      background: '#f8f9fa', 
                      borderRadius: '4px', 
                      fontSize: '13px', 
                      color: '#666',
                      border: '1px solid #dee2e6',
                      marginTop: '10px'
                    }}>
                      <strong>CSV Format:</strong> Include a column named <code>address</code>, <code>digipin</code>, or <code>latlng</code> (format: "lat,lng").
                      <br />
                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => downloadSampleCSV('/sample-csv/bulk-distance/address', 'sample_bulk_distance_address.csv')}
                          style={{ 
                            color: '#007bff', 
                            textDecoration: 'underline', 
                            fontWeight: '500',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 0',
                            fontSize: '13px'
                          }}
                        >
                          📥 Download Sample (Address)
                        </button>
                        <span style={{ color: '#ccc' }}>|</span>
                        <button
                          onClick={() => downloadSampleCSV('/sample-csv/bulk-distance/digipin', 'sample_bulk_distance_digipin.csv')}
                          style={{ 
                            color: '#007bff', 
                            textDecoration: 'underline', 
                            fontWeight: '500',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 0',
                            fontSize: '13px'
                          }}
                        >
                          📥 Download Sample (DigiPin)
                        </button>
                        <span style={{ color: '#ccc' }}>|</span>
                        <button
                          onClick={() => downloadSampleCSV('/sample-csv/bulk-distance/latlng', 'sample_bulk_distance_latlng.csv')}
                          style={{ 
                            color: '#007bff', 
                            textDecoration: 'underline', 
                            fontWeight: '500',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px 0',
                            fontSize: '13px'
                          }}
                        >
                          📥 Download Sample (Lat/Lng)
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={!bulkFile || !bulkReference || bulkLoading}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (!bulkFile || !bulkReference || bulkLoading) ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      fontWeight: '500',
                      width: '100%',
                      marginBottom: '20px'
                    }}
                  >
                    {bulkLoading ? 'Processing...' : 'Calculate Distances'}
                  </button>
                </form>

                {bulkJobId && !bulkLoading && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    border: '1px solid #90caf9'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      ✓ Job Queued Successfully!
                    </div>
                    <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                      Your job has been submitted and is being processed in the background.
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      Please check the <strong>Job History</strong> tab to view progress and download results when complete.
                    </div>
                  </div>
                )}

                {bulkLoading && !bulkJobId && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      width: '100%', 
                      backgroundColor: '#e9ecef', 
                      borderRadius: '4px', 
                      overflow: 'hidden',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: `${bulkProgress}%`,
                        height: '20px',
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {bulkProgress}%
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                      Processing {bulkProgress}% complete...
                    </div>
                  </div>
                )}

                {bulkError && (
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    {bulkError}
                  </div>
                )}

                {bulkResultUrl && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    <h4>Processing Complete!</h4>
                    <p>Distance calculations completed successfully.</p>
                    <a
                      href={bulkResultUrl}
                      download="bulk_distance_results.csv"
                      style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        marginTop: '10px'
                      }}
                    >
                      Download Results
                    </a>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'distance-between' && (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '500' }}>Select CSV File:</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleDistanceBetweenFileChange}
                    disabled={isAnyProcessing}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      opacity: isAnyProcessing ? 0.6 : 1,
                      cursor: isAnyProcessing ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {distanceBetweenFile && (
                    <p style={{ color: '#28a745', fontSize: '14px' }}>
                      Selected: {distanceBetweenFile.name}
                    </p>
                  )}
                  <div style={{ 
                    padding: '12px', 
                    background: '#f8f9fa', 
                    borderRadius: '4px', 
                    fontSize: '13px', 
                    color: '#666',
                    border: '1px solid #dee2e6',
                    marginTop: '10px'
                  }}>
                    <strong>CSV Format:</strong> Include columns named <code>address1</code> and <code>address2</code>.
                    <br />
                    <button
                      onClick={() => downloadSampleCSV('/sample-csv/bulk-distance-between', 'sample_bulk_distance_between.csv')}
                      style={{ 
                        color: '#007bff', 
                        textDecoration: 'underline', 
                        fontWeight: '500',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 0',
                        fontSize: '13px',
                        marginTop: '8px',
                        display: 'inline-block'
                      }}
                    >
                      📥 Download Sample CSV
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleDistanceBetween}
                  disabled={!distanceBetweenFile || distanceBetweenLoading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (!distanceBetweenFile || distanceBetweenLoading) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    width: '100%',
                    marginBottom: '20px'
                  }}
                >
                  {distanceBetweenLoading ? 'Processing...' : 'Calculate Distances'}
                </button>

                {distanceBetweenJobId && !distanceBetweenLoading && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    border: '1px solid #90caf9'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      ✓ Job Queued Successfully!
                    </div>
                    <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                      Your job has been submitted and is being processed in the background.
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      Please check the <strong>Job History</strong> tab to view progress and download results when complete.
                    </div>
                  </div>
                )}

                {distanceBetweenLoading && !distanceBetweenJobId && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      width: '100%', 
                      backgroundColor: '#e9ecef', 
                      borderRadius: '4px', 
                      overflow: 'hidden',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: `${distanceBetweenProgress}%`,
                        height: '20px',
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {distanceBetweenProgress}%
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                      Processing {distanceBetweenProgress}% complete...
                    </div>
                  </div>
                )}

                {distanceBetweenError && (
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    {distanceBetweenError}
                  </div>
                )}

                {distanceBetweenResultUrl && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    <h4>Processing Complete!</h4>
                    <p>Distance calculations completed successfully.</p>
                    <a
                      href={distanceBetweenResultUrl}
                      download="distance_between_results.csv"
                      style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        marginTop: '10px'
                      }}
                    >
                      Download Results
                    </a>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'distance-all' && (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontSize: '14px', fontWeight: '500' }}>Select CSV File:</label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleDistanceAllFileChange}
                    disabled={isAnyProcessing}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      marginBottom: '10px',
                      opacity: isAnyProcessing ? 0.6 : 1,
                      cursor: isAnyProcessing ? 'not-allowed' : 'pointer'
                    }}
                  />
                  {distanceAllFile && (
                    <p style={{ color: '#28a745', fontSize: '14px' }}>
                      Selected: {distanceAllFile.name}
                    </p>
                  )}
                  <div style={{ 
                    padding: '12px', 
                    background: '#f8f9fa', 
                    borderRadius: '4px', 
                    fontSize: '13px', 
                    color: '#666',
                    border: '1px solid #dee2e6',
                    marginTop: '10px'
                  }}>
                    <strong>CSV Format:</strong> Include a column named <code>address</code>, <code>digipin</code>, or <code>latlng</code> (format: "lat,lng").
                    <br />
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => downloadSampleCSV('/sample-csv/bulk-distance-all/address', 'sample_bulk_distance_all_address.csv')}
                        style={{ 
                          color: '#007bff', 
                          textDecoration: 'underline', 
                          fontWeight: '500',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 0',
                          fontSize: '13px'
                        }}
                      >
                        📥 Download Sample (Address)
                      </button>
                      <span style={{ color: '#ccc' }}>|</span>
                      <button
                        onClick={() => downloadSampleCSV('/sample-csv/bulk-distance-all/digipin', 'sample_bulk_distance_all_digipin.csv')}
                        style={{ 
                          color: '#007bff', 
                          textDecoration: 'underline', 
                          fontWeight: '500',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 0',
                          fontSize: '13px'
                        }}
                      >
                        📥 Download Sample (DigiPin)
                      </button>
                      <span style={{ color: '#ccc' }}>|</span>
                      <button
                        onClick={() => downloadSampleCSV('/sample-csv/bulk-distance-all/latlng', 'sample_bulk_distance_all_latlng.csv')}
                        style={{ 
                          color: '#007bff', 
                          textDecoration: 'underline', 
                          fontWeight: '500',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px 0',
                          fontSize: '13px'
                        }}
                      >
                        📥 Download Sample (Lat/Lng)
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleDistanceAll}
                  disabled={!distanceAllFile || distanceAllLoading}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: (!distanceAllFile || distanceAllLoading) ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    width: '100%',
                    marginBottom: '20px'
                  }}
                >
                  {distanceAllLoading ? 'Processing...' : 'Find Closest Addresses'}
                </button>

                {distanceAllJobId && !distanceAllLoading && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    borderRadius: '4px',
                    marginBottom: '20px',
                    border: '1px solid #90caf9'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                      ✓ Job Queued Successfully!
                    </div>
                    <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                      Your job has been submitted and is being processed in the background.
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      Please check the <strong>Job History</strong> tab to view progress and download results when complete.
                    </div>
                  </div>
                )}

                {distanceAllLoading && !distanceAllJobId && (
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ 
                      width: '100%', 
                      backgroundColor: '#e9ecef', 
                      borderRadius: '4px', 
                      overflow: 'hidden',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: `${distanceAllProgress}%`,
                        height: '20px',
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {distanceAllProgress}%
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                      Processing {distanceAllProgress}% complete...
                    </div>
                  </div>
                )}

                {distanceAllError && (
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    {distanceAllError}
                  </div>
                )}

                {distanceAllResultUrl && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    borderRadius: '4px',
                    marginBottom: '20px'
                  }}>
                    <h4>Processing Complete!</h4>
                    <p>Closest address analysis completed successfully.</p>
                    <a
                      href={distanceAllResultUrl}
                      download="distance_all_addresses_results.csv"
                      style={{
                        display: 'inline-block',
                        padding: '8px 16px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        marginTop: '10px'
                      }}
                    >
                      Download Results
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>      
        </div>
      ) : (
        /* Job History Tab */
        <div style={{ width: '100%' }}>
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
              Job History
            </h3>
            <button
              onClick={fetchJobHistory}
              disabled={loadingHistory}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loadingHistory ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {loadingHistory ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {historyError && (
            <div style={{
              padding: '10px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              {historyError}
            </div>
          )}

          {loadingHistory ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading job history...
            </div>
          ) : (
            jobHistory.length === 0 ? (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                background: '#f8f9fa',
                borderRadius: '8px',
                color: '#666'
              }}>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>No job history found</p>
                <p style={{ fontSize: '14px' }}>Your bulk processing jobs will appear here once you start processing files.</p>
              </div>
            ) : (
            <div style={{
              background: '#ffffff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333' }}>Job Type</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333' }}>File Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333' }}>Progress</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333' }}>Created</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: '#333' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobHistory.map((job) => {
                      const jobTypeName = {
                        'bulk-csv': 'Data Conversion',
                        'bulk-distance': 'Distance Calculation',
                        'bulk-distance-between': 'Point-to-Point Distance',
                        'bulk-distance-all': 'Closest Address Finder'
                      }[job.job_type] || job.job_type;
                      
                      const statusColor = {
                        'completed': '#28a745',
                        'processing': '#007bff',
                        'error': '#dc3545',
                        'pending': '#ffc107'
                      }[job.status] || '#6c757d';
                      
                      const statusText = {
                        'completed': 'Completed',
                        'processing': 'Processing',
                        'error': 'Error',
                        'pending': 'Pending'
                      }[job.status] || job.status;
                      
                      const createdDate = new Date(job.created_at).toLocaleString();
                      
                      return (
                        <tr key={job.job_id} style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#333' }}>{jobTypeName}</td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#333' }}>
                            {job.filename || 'N/A'}
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              backgroundColor: statusColor + '20',
                              color: statusColor,
                              fontWeight: '500',
                              fontSize: '12px'
                            }}>
                              {statusText}
                            </span>
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#333' }}>
                            {job.progress}%
                          </td>
                          <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>
                            {createdDate}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            {(job.status === 'completed' && job.result_filename) ? (
                              <button
                                onClick={() => downloadJobResult(job.job_id, job.result_filename)}
                                style={{
                                  padding: '6px 12px',
                                  backgroundColor: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                              >
                                Download
                              </button>
                            ) : (job.status === 'error' ? (
                              <span style={{ fontSize: '12px', color: '#dc3545' }}>
                                {job.error_message ? job.error_message.substring(0, 30) + '...' : 'Error occurred'}
                              </span>
                            ) : (
                              <span style={{ fontSize: '12px', color: '#666' }}>-</span>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default BulkCSV;
